import { Location } from '@angular/common';
import {
  HttpClient,
  HttpEventType,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import * as Excel from 'exceljs/dist/exceljs.min.js';
import * as FileSaver from 'file-saver';
import * as moment from 'moment';
import swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { environment } from '../../../environments/environment';
import { BranchService } from '../branch/branch.service';
import { UiService } from '../ui/ui.service';
import { UserService } from '../user/user.service';

const EXCEL_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

declare var $;
declare var loadImage;

export interface ExcelWorksheet {
  name: string;
  json: any[];
  keys: string[];

  extras?: any;
}

@Injectable()
export class HelperService {
  private focusNextInputLoading = false;

  public capstoneBranches = [2];
  public diplomaPaperText = new Proxy(
    {
      singular: null,
      plural: null,
      toString: () => { },
    },
    {
      get: (target, prop) => {
        const toString = () => {
          const branchId = this.branchService.currentProgrammeBranchId;
          // console.log(branchId, prop);
          if (!branchId) {
            return '';
          }
          const isCapstone = this.capstoneBranches.some(
            (branch) => branch == branchId
          );
          if (isCapstone) {
            return prop === 'plural' ? 'Capstone Projects' : 'Capstone Project';
          }
          return prop === 'plural' ? 'Diploma Papers' : 'Diploma Paper';
        };
        return prop === 'toString' ? toString : toString();
      },
    }
  );

  constructor(
    private router: Router,
    private user: UserService,
    private location: Location,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private ui: UiService,
    private branchService: BranchService
  ) { }

  get baseRoute() {
    // console.log(this.router.url);
    return ``;
  }

  static convertDate(date, onlyDate = false) {
    if (moment(date).isValid()) {
      return onlyDate
        ? moment(date).format('YYYY-MM-DD')
        : moment(date).format('YYYY-MM-DD HH:mm:ss');
    } else {
      return '';
    }
  }

  static isValidURL(str) {
    const pattern = new RegExp(
      '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
      'i'
    ); // fragment locator
    return !!pattern.test(str);
  }

  static print(html) {
    const win = window.open('', 'PRINT', '');
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  }

  static textareaToHTML(str) {
    return str.replace(/\r?\n/g, '<br />');
  }

  static copyToClipboard(text) {
    console.log(text);
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = text;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }

  static setTimezoneOffset(date: Date) {
    const offset = new Date().getTimezoneOffset();
    return new Date(date.getTime() - offset * 60000);
  }

  static exportExcel(filename: string, data: string[][]) {
    const ws_name = 'Sheet';
    const wb = XLSX.utils.book_new(),
      ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [{ wch: 30 }, { wch: 30 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, ws, ws_name);
    XLSX.writeFile(wb, filename);
  }

  static titleCase(value) {
    return value
      .split(/\s+/)
      .map(
        (str) =>
          (str && str[0] && String(str[0]).toUpperCase()) +
          (str && String(str).slice(1).toLowerCase())
      )
      .join(' ');
  }

  navigateBack() {
    this.location.back();
  }

  getSafeFileUrl(file) {
    return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
  }

  getDownloadablePath(file) {
    const filename = '/' + file.enc_name + '.' + file.extension;
    const filepath = file.tmp_path
      ? file.tmp_path + filename
      : file.path + filename;
    return environment.apiUrl + '/' + filepath;
  }

  downloadFile(file) {
    const name = '/' + file.enc_name + '.' + file.extension;
    const pathToFile = file.tmp_path ? file.tmp_path + name : file.path + name;
    this.ui.loader.show();
    const req = new HttpRequest(
      'POST',
      '/download',
      { pathToFile },
      { responseType: 'blob', reportProgress: true }
    );
    // this.http.post('/download', { pathToFile }, { responseType: 'blob' })
    this.http.request(req).subscribe(
      (event: any) => {
        if (event.type === HttpEventType.DownloadProgress) {
          console.log(Math.floor((event.loaded / event.total) * 100));
        } else if (event instanceof HttpResponse) {
          this.ui.loader.hide();
          const downloadableName = file.title
            ? file.title + '.' + file.extension
            : file.name;
          FileSaver.saveAs(event.body, downloadableName);
        }
      },
      (err) => {
        this.ui.loader.hide();
        console.log(err);
      }
    );
  }

  fromRoman(str): number {
    if (!str) {
      return 0;
    }
    let result = 0;
    // the result is now a number, not a string
    const decimal = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
    const roman = [
      'M',
      'CM',
      'D',
      'CD',
      'C',
      'XC',
      'L',
      'XL',
      'X',
      'IX',
      'V',
      'IV',
      'I',
    ];
    decimal.forEach((value, i) => {
      while (str.indexOf(roman[i]) === 0) {
        // checking for the first characters in the string
        result += value;
        // adding the decimal value to our result counter
        str = str.replace(roman[i], '');
        // remove the matched Roman letter from the beginning
      }
    });
    return result;
  }

  fromRomanOrNumToNumber(str): number {
    const validatorNumber = /^\d+$/;
    if (validatorNumber.test(str)) {
      return Number(str);
    } else {
        if (!str) {
          return 0;
        }
        let result = 0;
        // the result is now a number, not a string
        const decimal = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
        const roman = [
          'M',
          'CM',
          'D',
          'CD',
          'C',
          'XC',
          'L',
          'XL',
          'X',
          'IX',
          'V',
          'IV',
          'I',
        ];
        decimal.forEach((value, i) => {
          while (str.indexOf(roman[i]) === 0) {
            // checking for the first characters in the string
            result += value;
            // adding the decimal value to our result counter
            str = str.replace(roman[i], '');
            // remove the matched Roman letter from the beginning
          }
        });
        return result;
    }
  }

  getProfileImageUrl(image) {
    return image
      ? environment.apiUrl + '/' + image
      : '../assets/img/icon-no-image.png';
  }

  getFromConstant(key, callback) {
    this.http.get('/bmi-others/constant/' + key).subscribe((res) => {
      callback(null, res);
    }, callback);
  }

  validateDate(date) {
    return date
      ? date instanceof Date
        ? date
        : new Date(date.replace(/-/g, '/'))
      : null;
  }

  loadListTillEnd(fetchCallback, currentPage, totalPages, endCallback = null) {
    if (currentPage >= totalPages) {
      return endCallback && endCallback();
    }
    this.ui.loader.show('helper.service.ts => loadListTillEnd');
    fetchCallback(({ currentPage, totalPages }) => {
      this.ui.loader.hide('helper.service.ts => loadListTillEnd');
      this.loadListTillEnd(fetchCallback, currentPage, totalPages, endCallback);
    });
  }

  focusNextInput(
    event,
    list,
    currentPage,
    totalPages,
    loadFn,
    otherwiseFn = null
  ) {
    const targetId = event.target.id.split('-');
    if (event.keyCode === 40) {
      const nextIndex = Number(targetId.pop()) + 1;
      const nextInputId = [...targetId, nextIndex].join('-');
      if (nextIndex >= list.length) {
        if (currentPage !== totalPages) {
          this.ui.loader.show();
          loadFn(() => {
            this.ui.loader.hide();
            document.getElementById(nextInputId).focus();
          });
        }
      } else {
        document.getElementById(nextInputId).focus();
      }
      return;
    } else if (event.keyCode === 38) {
      const prevIndex = Number(targetId.pop()) - 1;
      const prevInputId = [...targetId, prevIndex].join('-');
      if (prevIndex >= 0) {
        document.getElementById(prevInputId).focus();
      }
      return;
    }
    if (otherwiseFn) {
      otherwiseFn();
    }
  }

  handleError(err, type) {
    if (err) {
      if (Object.keys(err).length) {
        swal(this.toTitleCase(type), err[Object.keys(err)[0]], type);
      }
    }
    return true;
  }

  toTitleCase(str: string) {
    return str[0].toUpperCase() + str.slice(1);
  }

  public exportAsExcelFile(
    data: ExcelWorksheet[],
    excelFileName: string
  ): void {
    const workbook = new Excel.Workbook();
    data.forEach((data: ExcelWorksheet) => {
      const worksheet = workbook.addWorksheet(data.name);
      worksheet.columns = data.keys.map((key: any) => ({
        header: typeof key === 'string' ? key : key.value,
        key: typeof key === 'string' ? key : key.value,
        width: typeof key === 'string' ? 10 : key.width,
        // width: data.json.reduce((result, data) => {
        //   if (data[key]) {
        //     return result > String(data[key]).length ? result : String(data[key]).length;
        //   }
        //   return result;
        // }, key.length > 10 ? key.length : 10)
      }));
      worksheet.addRows(data.json);
      worksheet.getRow(1).eachCell((cell) => {
        cell.style = {
          font: {
            bold: true,
            name: 'Calibri',
            // color: { argb: data.extras && data.extras.headerFontColor || 'FF000000' }
          },
        };
        // cell.fill = {
        //   type: 'pattern',
        //   pattern: 'solid',
        //   fgColor: { argb: data.extras && data.extras.headerForegorund || 'FFFFFFFF' }
        // };
      });
      if (data.extras && data.extras.rating) {
        worksheet.getRow(1 + data.json.length).eachCell((cell) => {
          cell.style = {
            font: {
              bold: true,
              name: 'Calibri',
              color: {
                argb:
                  (data.extras && data.extras.headerFontColor) || 'FF000000',
              },
            },
          };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {
              argb: (data.extras && data.extras.headerForegorund) || 'FFFFFFFF',
            },
          };
        });
      }
      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.alignment = {
            wrapText: true,
            vertical: 'top',
            horizontal: (data.extras && data.extras.textAlignment) || 'left',
          };
        });
        // row.height = 125;
      });
      worksheet.state = 'visible';
    });
    this.saveAsExcelFile(workbook, excelFileName);
  }

  public saveAsExcelFile(workbook: Excel.Workbook, fileName: string): void {
    workbook.xlsx.writeBuffer().then((buffer) => {
      const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
      FileSaver.saveAs(
        data,
        fileName.replace(/\s/g, '_') +
        '_export_' +
        new Date().getTime() +
        EXCEL_EXTENSION
      );
    });
  }

  public pushNotify() {
    // http://192.168.1.59:8000/api/bmi-admin/course?name=anis&data=hi
    const data = 'hi';
    this.http.get('/course?name=&data=' + data).subscribe();
  }

  public parseNotification(notification, data, relation) {
    const { notification_constant } = notification;
    const { title } = notification_constant.constant;
    const commonFields = {
      id: notification.id,
      type: notification_constant.key,
      data: {
        ...data,
        id: notification.fk,
      },
      user: notification.user,
      time: this.getTimeElapsed(moment(notification.created_at)),
    };
    const reducerObj = {
      peer_evaluation: () => {
        const rlp =
          (relation['peer_group'] &&
            relation['peer_group']['real_life_project'].name) ||
          data.rlp;
        return {
          ...commonFields,
          title: 'Peer Evaluation',
          body: rlp,
        };
      },
      submit_course_evaluation: () => ({
        ...commonFields,
        title: 'Course Evaluation',
        body: `${HelperService.titleCase(
          notification.user.name
        )} evaluated the course, "${relation['name'] || data.name}"`,
        // title: title.replace(/#course/g, data.name),
      }),
      mobility_programme_registered: () => ({
        ...commonFields,
        title: 'Mobility Programme',
        body: `${HelperService.titleCase(
          notification.user.name
        )} registered to the mobility programme, "${relation.name || data.name
          }"`,
        // title: title.replace(/#course/g, data.name),
      }),
      mobility_programme_unregistered: () => ({
        ...commonFields,
        title: 'Mobility Programme',
        body: `${HelperService.titleCase(
          notification.user.name
        )} unregistered from the mobility programme, "${relation.name || data.name
          }"`,
        // title: title.replace(/#course/g, data.name),
      }),
      mobility_programme_requested: () => ({
        ...commonFields,
        title: 'Mobility Programme',
        body: `${HelperService.titleCase(
          notification.user.name
        )} requested for registration to the mobility programme, "${relation.name || data.name
          }"`,
        // title: title.replace(/#course/g, data.name),
      }),
      mobility_programme_interest: () => ({
        ...commonFields,
        title: 'Mobility Programme',
        body: `${HelperService.titleCase(
          notification.user.name
        )} shown interest for "${relation.name || data.name}"`,
        // title: title.replace(/#course/g, data.name),
      }),
      elective_and_seminar_registered: () => ({
        ...commonFields,
        title: 'Elective & Field Seminar',
        body: `${HelperService.titleCase(
          notification.user.name
        )} registered to "${relation.name || data.name}"`,
        // title: title.replace(/#course/g, data.name),
      }),
      elective_and_seminar_unregistered: () => ({
        ...commonFields,
        title: 'Elective & Field Seminar',
        body: `${HelperService.titleCase(
          notification.user.name
        )} unregistered from "${relation.name || data.name}"`,
        // title: title.replace(/#course/g, data.name),
      }),
      elective_and_seminar_requested: () => ({
        ...commonFields,
        title: 'Elective & Field Seminar',
        body: `${HelperService.titleCase(
          notification.user.name
        )} requested for registration to "${relation.name || data.name}"`,
        // title: title.replace(/#course/g, data.name),
      }),
      elective_and_seminar_interest: () => ({
        ...commonFields,
        title: 'Elective & Field Seminar',
        body: `${HelperService.titleCase(
          notification.user.name
        )} shown interest for "${relation.name || data.name}"`,
        // title: title.replace(/#course/g, data.name),
      }),
      peer_evaluation_submitted: () => {
        const rlp =
          (relation &&
            relation['peer_group'] &&
            relation['peer_group']['real_life_project'] &&
            relation['peer_group']['real_life_project'].name) ||
          data.rlp;
        return {
          ...commonFields,
          title: 'Peer Evaluation',
          body: `${HelperService.titleCase(
            notification.user.name
          )} evaluated peers for "${rlp}"`,
          data: {
            ...commonFields.data,
            id: relation && relation.peer_group_id,
          },
          // title: title.replace(/#course/g, data.name),
        };
      },
      submit_rlp_evaluation: () => ({
        ...commonFields,
        title: 'Live Project Evaluation',
        body: `${HelperService.titleCase(
          notification.user.name
        )} evaluated the live project, "${relation['name'] || data.name}"`,
        // title: title.replace(/#course/g, data.name),
      }),
      submit_elective_and_seminar_evaluation: () => {
        const type = relation.type == 1 ? 'Elective' : 'Field Seminar';
        return {
          ...commonFields,
          title: type + ' Evaluation',
          body: `${HelperService.titleCase(
            notification.user.name
          )} evaluated the ${type}, "${relation['course_title'] || data.name}"`,
          // title: title.replace(/#course/g, data.name),
        };
      },
    };
    return reducerObj[notification_constant.key]
      ? reducerObj[notification_constant.key]()
      : '';
  }

  getTimeElapsed(from: moment.Moment) {
    return moment.duration(moment().diff(from)).humanize();
  }

  getSafeNavigationProxy(obj) {
    return new Proxy(obj, {
      get(_, key) {
        if (typeof key !== 'string') {
          return undefined;
        } else {
          const data = key
            .split('.')
            .reduce((result, K) => (result ? result[K] : undefined), obj);
          return data;
        }
      },
    });
  }

  validateExtension(file, extensions) {
    return extensions.includes(file.name.split('.').slice(-1)[0]);
  }

  hasKey(obj: object, key: string) {
    const splittedKeys = key.split('.');
    let testVar: any = obj;
    for (const K of splittedKeys) {
      if (!testVar[K]) {
        return false;
      }
      testVar = testVar[K];
    }
    return true;
  }

  loadImage(file): Promise<File> {
    return new Promise((resolve, reject) => {
      loadImage(
        file,
        (img) => {
          if (img.type === 'error') {
            reject('Error loading image');
          } else {
            img.toBlob((generatedBlob: Blob) => {
              resolve(new File([generatedBlob], file.name));
            });
          }
        },
        {
          canvas: true,
          orientation: true,
        }
      );
    });
  }

  getLinkUrl(link) {
    return link.url && link.url.startsWith('http')
      ? link.url
      : 'http://' + link.url;
  }

  gradeToNum(grade) {
    const table = {
      A: 96,
      B: 90,
      C: 80,
      D: 60,
      E: 50,
      F: 49,
    };

    if (grade === '') return 0;
    return table[String(grade).toUpperCase()];
  }

  numToGrade(num) {
    const table = {
      0: '',
      96: 'A',
      90: 'B',
      80: 'C',
      60: 'D',
      50: 'E',
      49: 'F',
    };

    if (table[num])
      return table[num];
    else
      return table[0];
  }

  overallPercentage(count, total) {
    const percentage = (count / total * 100).toFixed(2);

    return percentage;
  }

  percentageData(data) {
    const newData = data.map((exam) => {
      return {
        ...exam,
        grade_a_percentage: exam.a_count && exam.total_count ? this.overallPercentage(exam.a_count, exam.total_count) : 0.00,
        grade_b_percentage: exam.b_count && exam.total_count ? this.overallPercentage(exam.b_count, exam.total_count) : 0.00,
        grade_c_percentage: exam.c_count && exam.total_count ? this.overallPercentage(exam.c_count, exam.total_count) : 0.00,
        grade_d_percentage: exam.d_count && exam.total_count ? this.overallPercentage(exam.d_count, exam.total_count) : 0.00,
        grade_e_percentage: exam.e_count && exam.total_count ? this.overallPercentage(exam.e_count, exam.total_count) : 0.00,
        grade_f_percentage: exam.f_count && exam.total_count ? this.overallPercentage(exam.f_count, exam.total_count) : 0.00,
      }
    })

    return newData;
  }
}



// WEBPACK FOOTER //
// G:/UTL/BMI/Admin/Admin-ANG/src/app/services/helper/helper.service.ts